/**
 * Blackjack Multiplayer Table Component
 */

import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowLeft, Send, Crown, Eye, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useMultiplayerGame } from '../hooks/useMultiplayerGame'
import { TablePlayer, Card, calculateHandValue } from '../services/multiplayerService'
import { useAuth } from '@/features/auth/contexts/AuthContext'
import { cn } from '@/lib/utils'

interface MultiplayerTableProps {
  tableId: string
  onLeave: () => void
}

export function MultiplayerTable({ tableId, onLeave }: MultiplayerTableProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [betAmount, setBetAmount] = useState(100)
  const [chatInput, setChatInput] = useState('')
  const chatRef = useRef<HTMLDivElement>(null)

  const {
    tableData,
    isLoading,
    error,
    myPlayer,
    isCapo,
    isMyTurn,
    isSpectator,
    timeLeft,
    dealerValue,
    showDealerCards,
    canBet,
    canPlay,
    canDouble,
    handleStartRound,
    handlePlaceBet,
    handleHit,
    handleStand,
    handleDouble,
    handleLeave,
    handleSendChat,
  } = useMultiplayerGame(tableId)

  // Auto-scroll chat
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight
    }
  }, [tableData?.messages])

  // Handle leave
  const handleLeaveClick = async () => {
    await handleLeave()
    onLeave()
  }

  // Handle bet submit
  const handleBetSubmit = async () => {
    const result = await handlePlaceBet(betAmount)
    if (!result.success) {
      alert(result.error)
    }
  }

  // Handle chat submit
  const handleChatSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!chatInput.trim()) return
    await handleSendChat(chatInput.trim())
    setChatInput('')
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-casino-gold animate-pulse">{t('common.loading')}</div>
      </div>
    )
  }

  if (error || !tableData) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <p className="text-red-400">{error || 'Mesa no encontrada'}</p>
        <Button onClick={onLeave}>{t('common.back')}</Button>
      </div>
    )
  }

  const getGameStateText = () => {
    switch (tableData.gameState) {
      case 'waiting':
        return t('multiplayer.waitingPlayers')
      case 'betting':
        return t('multiplayer.placeBets')
      case 'playing':
        const turnPlayer = tableData.players.find((p) => p.seat === tableData.currentTurnSeat)
        return `${t('multiplayer.turnOf')} ${turnPlayer?.name || '...'}`
      case 'dealerTurn':
        return t('multiplayer.dealerTurn')
      case 'ended':
        return t('multiplayer.roundEnded')
      default:
        return ''
    }
  }

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-zinc-900 to-zinc-950">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/30 border-b border-zinc-800">
        <div className="flex items-center gap-3">
          <span className="text-xl">🃏</span>
          <span className="font-cinzel text-casino-gold">
            {tableData.name}
            {isCapo && ' 👑'}
            {isSpectator && ` (${t('multiplayer.spectatorMode')})`}
          </span>
        </div>
        <div className="flex items-center gap-4 text-sm text-gray-400">
          <span>{t('multiplayer.min')} ${tableData.minBet.toLocaleString()}</span>
          <span className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            {tableData.players.length}/{tableData.maxPlayers} {t('multiplayer.players')}
          </span>
        </div>
      </div>

      {/* Table Area */}
      <div className="flex-1 relative overflow-hidden">
        {/* Green felt background */}
        <div
          className="absolute inset-4 rounded-t-[200px] rounded-b-[20px] border-4 border-amber-900"
          style={{
            background: 'radial-gradient(ellipse at center, #1a6b1a 0%, #0d5c0d 50%, #094509 100%)',
            boxShadow: 'inset 0 0 100px rgba(0,0,0,0.3), 0 10px 30px rgba(0,0,0,0.5)',
          }}
        >
          {/* Leave button */}
          <Button
            variant="destructive"
            size="sm"
            className="absolute top-4 left-4 z-20"
            onClick={handleLeaveClick}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('multiplayer.leave')}
          </Button>

          {/* Spectators count */}
          {tableData.spectators.length > 0 && (
            <div className="absolute top-4 right-[220px] bg-indigo-900/50 border border-indigo-500 rounded-full px-3 py-1 text-xs text-indigo-300 z-20">
              <Eye className="w-3 h-3 inline mr-1" />
              {tableData.spectators.length} {t('multiplayer.watching')}
            </div>
          )}

          {/* Card deck visual - CENTRO SUPERIOR con mano del dealer */}
          <div className="absolute top-3 left-1/2 -translate-x-1/2 flex items-center gap-3 z-10">
            {/* Deck stack effect */}
            <div className="relative">
              <div className="absolute top-1 left-1 w-10 h-14 rounded-lg bg-gray-800/60" />
              <div className="absolute top-0.5 left-0.5 w-10 h-14 rounded-lg bg-gray-700/80" />
              <div
                className="relative w-10 h-14 rounded-lg shadow-lg overflow-hidden border-2 border-casino-gold/60"
                style={{
                  boxShadow: '2px 2px 0 #333, 4px 4px 0 #222, 6px 6px 8px rgba(0,0,0,0.5)',
                }}
              >
                <img
                  src="/img/card-back.png"
                  alt="Deck"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            {/* Dealer hand emoji */}
            <span className="text-2xl filter drop-shadow-lg" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
              🤚
            </span>
          </div>

          {/* Dealer Area - debajo del mazo */}
          <div className="absolute top-[72px] left-1/2 -translate-x-1/2 text-center">
            <div className="text-xs text-white/70 uppercase tracking-wider mb-2">
              {t('blackjack.dealer')}
            </div>
            <div className="flex justify-center gap-0 min-h-[70px]">
              {tableData.dealerHand.map((card, i) => (
                <CardComponent
                  key={i}
                  card={card}
                  faceDown={i === 1 && !showDealerCards}
                  index={i}
                />
              ))}
            </div>
            <div className="mt-2 text-white font-bold">{dealerValue > 0 && dealerValue}</div>
          </div>

          {/* Game State */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
            <div className="bg-black/60 text-casino-gold px-6 py-3 rounded-lg border border-casino-gold/30 text-center">
              <div className="text-lg font-bold uppercase tracking-wider">{getGameStateText()}</div>
            </div>
          </div>

          {/* Timer */}
          <AnimatePresence>
            {isMyTurn && timeLeft > 0 && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                className={cn(
                  'absolute top-1/2 left-1/2 -translate-x-1/2 translate-y-8 text-5xl font-bold z-10',
                  timeLeft <= 10 ? 'text-red-500 animate-pulse' : 'text-casino-gold'
                )}
              >
                {timeLeft}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Players Area */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-[90%] flex justify-center gap-3 flex-wrap">
            {Array.from({ length: tableData.maxPlayers }, (_, i) => {
              const seat = i + 1
              const player = tableData.players.find((p) => p.seat === seat)
              return (
                <PlayerSlot
                  key={seat}
                  seat={seat}
                  player={player}
                  isMe={player?.odIdBrief === user?.uid}
                  isActiveTurn={tableData.currentTurnSeat === seat && tableData.gameState === 'playing'}
                  isCapo={player?.odIdBrief === tableData.capoId}
                />
              )
            })}
          </div>

          {/* Chat */}
          <div className="absolute top-16 right-4 w-48 max-h-40 bg-black/90 rounded-lg border border-casino-gold/40 overflow-hidden z-20 flex flex-col">
            <div className="px-3 py-2 bg-casino-gold/20 text-casino-gold text-xs font-semibold uppercase tracking-wider border-b border-casino-gold/20">
              {t('multiplayer.chat')}
            </div>
            <div ref={chatRef} className="flex-1 overflow-y-auto p-2 text-xs space-y-1 max-h-20">
              {tableData.messages.slice(-10).map((msg, i) => (
                <div key={i} className="break-words">
                  <span className="text-casino-gold font-semibold">{msg.name}:</span>{' '}
                  <span className="text-gray-300">{msg.text}</span>
                </div>
              ))}
            </div>
            <form onSubmit={handleChatSubmit} className="flex border-t border-white/10">
              <Input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder={t('multiplayer.message')}
                className="flex-1 bg-transparent border-0 rounded-none text-xs h-8 focus-visible:ring-0"
                maxLength={50}
              />
              <Button type="submit" variant="ghost" size="sm" className="h-8 px-2">
                <Send className="w-3 h-3" />
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="px-4 py-4 bg-black/50 border-t border-zinc-800 flex justify-center gap-3">
        {isSpectator ? (
          <div className="flex items-center gap-4">
            <span className="bg-indigo-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
              <Eye className="w-4 h-4 inline mr-2" />
              {t('multiplayer.spectatorMode')}
            </span>
            <span className="text-gray-400 text-sm">
              {tableData.spectators.length}{' '}
              {tableData.spectators.length === 1
                ? t('multiplayer.spectatorWatching')
                : t('multiplayer.spectatorsWatching')}
            </span>
          </div>
        ) : tableData.gameState === 'waiting' ? (
          isCapo ? (
            tableData.players.length >= 1 && (
              <Button variant="gold" onClick={handleStartRound}>
                🎴 {t('multiplayer.startRound')}
              </Button>
            )
          ) : (
            <p className="text-gray-400">{t('multiplayer.waitingCapo')}</p>
          )
        ) : tableData.gameState === 'betting' ? (
          canBet ? (
            <div className="flex items-center gap-3">
              <Input
                type="number"
                value={betAmount}
                onChange={(e) => setBetAmount(Number(e.target.value))}
                min={tableData.minBet}
                step={50}
                className="w-28 text-center"
              />
              <Button variant="gold" onClick={handleBetSubmit}>
                {t('multiplayer.bet')}
              </Button>
            </div>
          ) : (
            <p className="text-green-400">
              {t('blackjack.betLabel')}: ${myPlayer?.currentBet?.toLocaleString()}
            </p>
          )
        ) : tableData.gameState === 'playing' ? (
          canPlay ? (
            <>
              <Button
                onClick={handleHit}
                className="bg-green-600 hover:bg-green-700"
              >
                🃏 {t('multiplayer.hit')}
              </Button>
              <Button
                onClick={handleStand}
                className="bg-red-600 hover:bg-red-700"
              >
                ✋ {t('multiplayer.standBtn')}
              </Button>
              {canDouble && (
                <Button
                  onClick={handleDouble}
                  className="bg-yellow-600 hover:bg-yellow-700 text-black"
                >
                  💰 {t('multiplayer.double')}
                </Button>
              )}
            </>
          ) : (
            <p className="text-gray-400">
              {t('multiplayer.waitingTurn')}{' '}
              {tableData.players.find((p) => p.seat === tableData.currentTurnSeat)?.name}...
            </p>
          )
        ) : tableData.gameState === 'dealerTurn' ? (
          <p className="text-casino-gold">{t('multiplayer.dealerTurn')}...</p>
        ) : tableData.gameState === 'ended' ? (
          isCapo ? (
            <Button variant="gold" onClick={handleStartRound}>
              🔄 {t('multiplayer.newRound')}
            </Button>
          ) : (
            <p className="text-gray-400">{t('multiplayer.waitingNewRound')}</p>
          )
        ) : null}
      </div>
    </div>
  )
}

// Card Component
function CardComponent({
  card,
  faceDown = false,
  index,
}: {
  card: Card
  faceDown?: boolean
  index: number
}) {
  const suitSymbols = { hearts: '♥', diamonds: '♦', clubs: '♣', spades: '♠' }
  const isRed = card.suit === 'hearts' || card.suit === 'diamonds'

  if (faceDown) {
    return (
      <motion.div
        initial={{ rotateY: 180 }}
        animate={{ rotateY: 0 }}
        transition={{ delay: index * 0.1 }}
        className="w-11 h-16 rounded overflow-hidden border border-casino-gold/60"
        style={{ marginLeft: index > 0 ? '-15px' : 0 }}
      >
        <img
          src="/img/card-back.png"
          alt="Card back"
          className="w-full h-full object-cover"
        />
      </motion.div>
    )
  }

  return (
    <motion.div
      initial={{ rotateY: 180, opacity: 0 }}
      animate={{ rotateY: 0, opacity: 1 }}
      transition={{ delay: index * 0.1 }}
      className={cn(
        'w-11 h-16 rounded bg-white flex flex-col justify-between p-1 shadow-lg',
        isRed ? 'text-red-600' : 'text-gray-900'
      )}
      style={{ marginLeft: index > 0 ? '-15px' : 0 }}
    >
      <span className="text-[10px] font-bold leading-none">
        {card.value}
        {suitSymbols[card.suit]}
      </span>
      <span className="text-[10px] font-bold leading-none self-end rotate-180">
        {card.value}
        {suitSymbols[card.suit]}
      </span>
    </motion.div>
  )
}

// Player Slot Component
function PlayerSlot({
  seat,
  player,
  isMe,
  isActiveTurn,
  isCapo,
}: {
  seat: number
  player?: TablePlayer
  isMe: boolean
  isActiveTurn: boolean
  isCapo: boolean
}) {
  const { t } = useTranslation()

  const getStatusClass = (status: string) => {
    const classes: Record<string, string> = {
      waiting: 'bg-zinc-700 text-gray-400',
      betting: 'bg-blue-600 text-white',
      ready: 'bg-blue-600 text-white',
      playing: 'bg-green-600 text-white',
      stand: 'bg-gray-600 text-white',
      bust: 'bg-red-600 text-white',
      blackjack: 'bg-casino-gold text-black',
      win: 'bg-green-600 text-white',
      lose: 'bg-red-600 text-white',
      push: 'bg-yellow-600 text-black',
    }
    return classes[status] || 'bg-zinc-700 text-gray-400'
  }

  const getStatusText = (status: string) => {
    const texts: Record<string, string> = {
      waiting: t('multiplayer.waiting'),
      betting: t('multiplayer.betting'),
      ready: t('multiplayer.betting'),
      playing: t('multiplayer.playing'),
      stand: t('multiplayer.stand'),
      bust: t('multiplayer.bust'),
      blackjack: t('multiplayer.blackjack'),
      win: t('multiplayer.win'),
      lose: t('multiplayer.lose'),
      push: t('multiplayer.push'),
    }
    return texts[status] || status
  }

  if (!player) {
    return (
      <div className="w-32 bg-black/30 border-2 border-dashed border-white/10 rounded-lg p-2 text-center opacity-50">
        <div className="text-xs text-white/70">
          {t('multiplayer.seat')} {seat}
        </div>
        <div className="h-16" />
        <div className="text-xs text-gray-500 mt-1">{t('multiplayer.empty')}</div>
      </div>
    )
  }

  const handValue = calculateHandValue(player.hand)

  return (
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className={cn(
        'w-32 bg-black/30 border-2 rounded-lg p-2 text-center transition-all',
        isMe && 'border-green-500',
        isActiveTurn && 'border-casino-gold shadow-lg shadow-casino-gold/30',
        !isMe && !isActiveTurn && 'border-white/10'
      )}
    >
      {/* Name */}
      <div className="text-xs text-white font-semibold flex items-center justify-center gap-1 truncate">
        {isCapo && <Crown className="w-3 h-3 text-casino-gold" />}
        {player.name}
        {isMe && ` (${t('multiplayer.you')})`}
      </div>

      {/* Hand */}
      <div className="flex justify-center gap-0 min-h-[50px] my-2">
        {player.hand.map((card, i) => (
          <CardComponent key={i} card={card} index={i} />
        ))}
      </div>

      {/* Bet */}
      {player.currentBet > 0 && (
        <div className="text-xs text-casino-gold">${player.currentBet.toLocaleString()}</div>
      )}

      {/* Score */}
      {handValue > 0 && <div className="text-xs text-white mt-1">{handValue}</div>}

      {/* Status */}
      <div className={cn('text-[10px] px-2 py-0.5 rounded-full mt-1 inline-block', getStatusClass(player.status))}>
        {getStatusText(player.status)}
      </div>
    </motion.div>
  )
}
